import { useEffect, useState } from "react";

import { atom, useAtom } from "jotai";
import { toast } from "sonner";

import { notifyDemoStoreMembershipChanged } from "src/lib/demoStore";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import {
  DELETE_INTEGRATION_MUTATION,
  INTEGRATION_MUTATION,
  INTEGRATION_SOURCES_QUERY,
  INTEGRATIONS_QUERY,
  LEAVE_INTEGRATION_MUTATION,
} from "src/lib/queries/integrations";

const fallbackAtom = atom(null);
const integrationsStoreAtom = atom({ loading: false });
const sourcesAtom = atom({});

/**
 * Custom hook to fetch and manage integrations of specific type
 * @param typeConfig {import("src/lib/constants").storeIntegrationTypes} Integration type configuration
 * @param [config] {Object} Configuration object
 * @param [config.doPolling] {boolean} Enable polling for syncing status
 * @param [config.queryOnRender] {boolean} Whether to query on initial render. True by default
 * @param [config.onLeaveSuccess] {Function} Callback invoked after a store is left successfully
 * @returns Integration hook
 */
export const useIntegration = (
  typeConfig,
  { doPolling, queryOnRender = true, onLeaveSuccess } = { queryOnRender: true }
) => {
  // Store integrations by type in atomic state so it can be shared across components
  // without having to hit the server again
  const [integrationsStore, setIntegrationsStore] = useAtom(
    integrationsStoreAtom
  );

  const [selectedId, setSelectedId] = useAtom(
    typeConfig?.selectedAtom || fallbackAtom
  );
  const [sources, setSources] = useAtom(sourcesAtom);
  const type = typeConfig?.id?.toUpperCase();

  const [loadedOnce, setLoadedOnce] = useState(false);
  const {
    queryApi: queryIntegrations,
    data,
    loading: queryIntegrationsLoading,
  } = useApiQuery({
    query: INTEGRATIONS_QUERY,
    onSuccess: (data) => {
      setLoadedOnce(true);
      setIntegrationsStore((prev) => ({
        ...prev,
        [type]: data.integrations,
      }));

      if (
        selectedId &&
        !data.integrations.find((integration) => integration.id === selectedId)
      ) {
        if (data.integrations.length > 0) {
          setSelectedId(data.integrations[0].id);
        } else {
          setSelectedId(null);
        }
      }

      if (!selectedId && data.integrations.length > 0) {
        setSelectedId(data.integrations[0].id);
      }
    },
  });

  // Update the integrations store when data changes which can
  // happen from anywhere else in the app where the integration is
  // returned from the server (query or mutation)
  useEffect(() => {
    if (type && data?.integrations) {
      setIntegrationsStore((prev) => ({
        ...prev,
        [type]: data.integrations,
      }));
    }
  }, [data, setIntegrationsStore, type]);

  const refetch = () => queryIntegrations({ type });
  const selectedIntegration = integrationsStore[type]?.find(
    (integration) => integration.id === selectedId
  );

  const { mutationApi: updateIntegration, loading: updateIntegrationLoading } =
    useApiMutation({
      mutation: INTEGRATION_MUTATION,
      onError: () => {
        toast.error("Error updating integration", {
          description: "Please try again",
        });

        return null;
      },
    });
  const { mutationApi: deleteIntegration, loading: deleteIntegrationLoading } =
    useApiMutation({
      mutation: DELETE_INTEGRATION_MUTATION,
      onError: () => {
        toast.error("Error deleting integration", {
          description: "Please try again",
        });

        return null;
      },
      onSuccess: async () => {
        await refetch();

        toast.success("Integration deleted successfully", {
          description: "The integration has been removed",
        });
      },
    });
  const { mutationApi: leaveIntegration, loading: leaveIntegrationLoading } =
    useApiMutation({
      mutation: LEAVE_INTEGRATION_MUTATION,
      onError: (error) => {
        toast.error("Error leaving store", {
          description: error?.message || "Please try again",
        });

        return null;
      },
      onSuccess: async () => {
        try {
          await refetch();
        } finally {
          notifyDemoStoreMembershipChanged();
          onLeaveSuccess?.();
        }

        toast.success("Store left", {
          description: "The store has been removed from your workspace.",
        });
      },
    });
  const { queryApi: sourcesQueryApi, loading: sourcesQueryApiLoading } =
    useApiQuery({
      query: INTEGRATION_SOURCES_QUERY,
    });

  // query integrations on render
  useEffect(() => {
    if (queryOnRender) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, queryOnRender]);

  const hasSyncingSelectedIntegration =
    selectedIntegration?.syncing ||
    ["PENDING", "RUNNING", "RETRYING"].includes(
      selectedIntegration?.analyticsStatus?.status
    ) ||
    selectedIntegration?.children?.some((integration) => integration.syncing);

  // Keep background freshness current; poll faster during visible imports.
  useEffect(() => {
    if (doPolling && selectedIntegration?.id) {
      const internal = setInterval(
        () => {
          refetch();
        },
        hasSyncingSelectedIntegration ? 5000 : 60_000
      );

      return () => clearInterval(internal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doPolling, hasSyncingSelectedIntegration, selectedIntegration?.id]);

  const querySources = async (integration) => {
    const { data } = await sourcesQueryApi({
      id: integration.id,
    });

    if (!data?.integrationSources) {
      return;
    }

    setSources({
      ...sources,
      [integration.id]: data.integrationSources.map((source) => ({
        ...source,
        enabled: integration.settings?.sources?.some(
          ({ id }) => id === source.id
        ),
      })),
    });
  };

  const loading =
    (queryIntegrationsLoading && (!doPolling || !loadedOnce)) ||
    updateIntegrationLoading ||
    deleteIntegrationLoading ||
    leaveIntegrationLoading ||
    sourcesQueryApiLoading;

  return {
    selectedIntegration,
    setSelectedIntegration: ({ id }) => {
      setSelectedId(id);
    },
    integrations: integrationsStore[type] || [],
    updateIntegration,
    deleteIntegration,
    leaveIntegration,
    querySources,
    sources,
    refetch,
    loading,
    hasLoadedOnce: loadedOnce,
  };
};
